import { castArray } from 'lodash';
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';

import { SortDirection } from '@air/api/types';

import { type QueryType } from '~/constants/search';
import { setQueryParams } from '~/utils/PathUtils';
import { type DELETED_ITEM_TYPES } from '../constants/itemTypes';
import { RECENTLY_DELETED_DEFAULT_SORT_FIELD, RECENTLY_DELETED_SORT_DIRECTIONS } from '../constants/sort';
import { type RecentlyDeletedItemType, type RecentlyDeletedSortField } from '../types';
import { useRecentlyDeletedItemTypes } from './useRecentlyDeletedItemTypes';

const isDeletedItemType = (
  value: string,
  deletedItemTypes: typeof DELETED_ITEM_TYPES,
): value is RecentlyDeletedItemType => deletedItemTypes.some((itemType) => itemType.id === value);

const getFilteredTypes = (rawTypes: string[], deletedItemTypes: typeof DELETED_ITEM_TYPES) => {
  const sanitizedFilteredTypes = rawTypes.filter((type) => isDeletedItemType(type, deletedItemTypes));

  return sanitizedFilteredTypes.length ? sanitizedFilteredTypes : deletedItemTypes.map((itemType) => itemType.id);
};

const getSortDirection = (value?: string | string[]): SortDirection => {
  const [firstValue] = castArray(value);
  return RECENTLY_DELETED_SORT_DIRECTIONS.includes(firstValue as SortDirection)
    ? (firstValue as SortDirection)
    : SortDirection.desc;
};

const getSortField = (_?: string | string[]): RecentlyDeletedSortField => {
  // Currently, we only support sorting by `createdAt` field.
  return RECENTLY_DELETED_DEFAULT_SORT_FIELD;
};

export const useRecentlyDeletedQueryParam = () => {
  const { query } = useRouter();
  const deletedItemTypes = useRecentlyDeletedItemTypes();

  useEffect(() => {
    const rawTypesFromQueryParams = castArray(query.type);
    const filteredTypes = getFilteredTypes(rawTypesFromQueryParams, deletedItemTypes);

    const rawSortFieldFromQueryParams = query.sortBy;
    const sanitizedSortField = getSortField(rawSortFieldFromQueryParams);

    const rawSortDirectionFromQueryParams = query.sortDir;
    const sanitizedSortDirection = getSortDirection(rawSortDirectionFromQueryParams);

    const sanitizedQueryParams: QueryType = {
      type: filteredTypes.length !== rawTypesFromQueryParams.length ? filteredTypes : rawTypesFromQueryParams,
      sortBy: sanitizedSortField,
      sortDir: sanitizedSortDirection,
    };

    if (
      filteredTypes.length !== rawTypesFromQueryParams.length ||
      sanitizedSortField !== rawSortFieldFromQueryParams ||
      sanitizedSortDirection !== rawSortDirectionFromQueryParams
    ) {
      setQueryParams(sanitizedQueryParams, 'replace');
    }
  }, [deletedItemTypes, query.sortBy, query.sortDir, query.type]);

  const values = useMemo(() => {
    const filteredTypes = getFilteredTypes(castArray(query.type), deletedItemTypes);
    const isFilterApplied = filteredTypes.length < deletedItemTypes.length;

    return {
      filteredTypes,
      isFilterApplied,
      sortField: getSortField(query.sortBy),
      sortDirection: getSortDirection(query.sortDir),
      setFilterTypes: (toggledType: RecentlyDeletedItemType) => {
        const newType = filteredTypes.includes(toggledType)
          ? filteredTypes.filter((type) => type !== toggledType)
          : [...filteredTypes, toggledType];
        setQueryParams({ type: newType }, 'push');
      },
      setSort: (sortField: RecentlyDeletedSortField, sortDirection: SortDirection) => {
        setQueryParams({ sortBy: sortField, sortDir: sortDirection }, 'push');
      },
    };
  }, [deletedItemTypes, query.sortBy, query.sortDir, query.type]);

  return values;
};
